import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { getAdminSettings, updateAdminSettings } from '../lib/api';
import {
  coerceBlacklistEntries,
  formatBlacklistPhone,
  normalizeBlacklistPhone,
} from '../lib/waPhoneBlacklist';
import { toast } from 'sonner';
import { ListChecks, Plus, X } from 'lucide-react';

export default function WaPhoneWhitelistCard() {
  const [settingsSnapshot, setSettingsSnapshot] = useState(null);
  const [whitelist, setWhitelist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');
  const [nameInput, setNameInput] = useState('');

  const loadWhitelist = useCallback(async () => {
    try {
      const data = await getAdminSettings();
      setSettingsSnapshot(data);
      setWhitelist(coerceBlacklistEntries(data.wa_phone_whitelist));
    } catch {
      toast.error('No se pudo cargar la lista blanca');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadWhitelist();
  }, [loadWhitelist]);

  const persistWhitelist = async (nextList) => {
    if (!settingsSnapshot) {
      toast.error('Configuración aún no cargada');
      return null;
    }
    const payload = {
      ...settingsSnapshot,
      appointment_reminder_minutes_before: Number(settingsSnapshot.appointment_reminder_minutes_before),
      appointment_reminder_check_interval_sec: Number(
        settingsSnapshot.appointment_reminder_check_interval_sec,
      ),
      appointment_daily_reminder_time: (settingsSnapshot.appointment_daily_reminder_time || '').trim(),
      wa_phone_whitelist: nextList,
    };
    const saved = await updateAdminSettings(payload);
    setSettingsSnapshot(saved);
    setWhitelist(coerceBlacklistEntries(saved.wa_phone_whitelist));
    return saved;
  };

  const handleAddPhone = async () => {
    const norm = normalizeBlacklistPhone(phoneInput);
    if (!norm) {
      toast.error('Escribe un número válido (10 dígitos, con o sin +52)');
      return;
    }
    if (whitelist.some((e) => e.phone === norm)) {
      toast.message('Ese número ya está en la lista blanca');
      return;
    }
    const name = String(nameInput || '').trim().slice(0, 80);
    try {
      await persistWhitelist([...whitelist, { phone: norm, name }]);
      setPhoneInput('');
      setNameInput('');
      toast.success('Número añadido a la lista blanca');
    } catch {
      toast.error('No se pudo guardar la lista blanca');
    }
  };

  const handleRemovePhone = async (phone) => {
    try {
      await persistWhitelist(whitelist.filter((e) => e.phone !== phone));
      toast.success('Número quitado de la lista blanca');
    } catch {
      toast.error('No se pudo actualizar la lista blanca');
    }
  };

  return (
    <Card className="mt-8 max-w-xl border border-border" data-testid="wa-phone-whitelist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ListChecks className="w-5 h-5 text-primary" />
          Lista blanca WhatsApp
        </CardTitle>
        <CardDescription>
          Si añades números aquí, el bot <strong>solo responderá a estos</strong> (con IA o con el menú
          automático, según esté encendida la IA). El resto de mensajes se ignoran. Si la lista está{' '}
          <strong>vacía, el bot responde a todos</strong> (comportamiento normal). Útil para pruebas o
          lanzamientos controlados. Se guardan los últimos 10 dígitos (México) y el cambio se aplica al
          instante.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Cargando lista blanca…</p>
        ) : (
          <>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[160px] space-y-2">
                <Label htmlFor="wa-whitelist-name">Nombre o nota (opcional)</Label>
                <Input
                  id="wa-whitelist-name"
                  type="text"
                  placeholder="Ej. Recepción, paciente prueba"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  maxLength={80}
                  className="h-11"
                />
              </div>
              <div className="flex-1 min-w-[200px] space-y-2">
                <Label htmlFor="wa-whitelist-input">Teléfono permitido</Label>
                <Input
                  id="wa-whitelist-input"
                  type="tel"
                  inputMode="numeric"
                  placeholder="771 234 5678 o 527712345678"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void handleAddPhone();
                    }
                  }}
                  className="h-11"
                />
              </div>
              <Button type="button" variant="outline" className="gap-2" onClick={() => void handleAddPhone()}>
                <Plus className="w-4 h-4" />
                Añadir
              </Button>
            </div>
            {whitelist.length > 0 ? (
              <ul className="space-y-2">
                {whitelist.map((entry) => (
                  <li
                    key={entry.phone}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 bg-muted/30"
                  >
                    <div className="min-w-0">
                      {entry.name ? (
                        <p className="text-sm font-medium text-foreground truncate">{entry.name}</p>
                      ) : null}
                      <p
                        className={`font-mono text-sm ${entry.name ? 'text-muted-foreground' : 'text-foreground'}`}
                      >
                        {formatBlacklistPhone(entry.phone)}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive shrink-0"
                      onClick={() => void handleRemovePhone(entry.phone)}
                      aria-label={`Quitar ${entry.name || entry.phone}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                Lista blanca vacía: el bot responde a todos los números.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
